import { Box, Flex, Table } from '@chakra-ui/react';
import { useEffect, useMemo, useState } from 'react';
import type { ISelectedItem } from '@/components/Atoms/FaceliftAccordion/FaceliftAccordion.types';
import FaceliftButton from '@/components/Atoms/FaceliftButton';
import FaceliftDialog from '@/components/Atoms/FaceliftDialog';
import type { SelectItem } from '@/components/Atoms/FaceliftSelect/FaceliftSelect.types';
import FaceliftText from '@/components/Atoms/FaceliftText';
import FaceliftDatePicker, { type DateRange } from '@/components/Molecules/FaceliftDatePicker';
import Show from '@/components/Molecules/Show';
import type {
	Column,
	ColumnFiltersProps,
	DataTableProps,
	SortOption,
} from '@/components/Organisms/DataTable/DataTable.types';
import DataTableFilters from '@/components/Organisms/DataTable/DataTableFilters';
import DataTableInner from '@/components/Organisms/DataTable/DataTableInner';
import DataTablePaginations from '@/components/Organisms/DataTable/DataTablePaginations';
import { colors } from '@/components/ui/theme';
import { useDateFilterStore } from '@/store/dateFilterStore';
import { getDateRange, getYearToDateRange } from '@/utils/dateUtils';
import { generateCSVFromTable, generateExcelFromTable } from '@/utils/exportFiles';
import { scheduleBackgroundExport } from '@/utils/scheduleBackgroundExport';

const DataTable = ({
	ref,
	addButtonRef,
	exportButtonRef,
	selectColumnRef,
	onSelectColumnRefReady,
	topLabel,
	loading = false,
	isSelectable,
	selectedItems = [],
	bordered,
	totalCount,
	columns = [],
	items = [],
	customExportItemsData = [],
	globalActions = [],
	// showTableFilterActions = false,
	searchable = false,
	hideShowColumn = false,
	expandable = false,
	fullscreen = false,
	exportable = false,
	globalFilter,
	paginated = false,
	creatableFn,
	statusFilters = [],
	actions = [],
	onFilterApply,
	onSelection = () => { },
	csv = false,
	excel = false,
	pdf = false,
	exportAllAsCSV,
	exportAllAsExcel,
	exportAllAsPDF,
	requiredColumnsForExpand,
	tableName = '',
	hideAllStatusFilter,
	hideAllColumnFilters = false,
	actionMenuOpenOverride = false,
	moduleName,
	globalDateFilters = [],
}: DataTableProps) => {
	const getFilterByModule = useDateFilterStore((state) => state.getFilterByModule);

	// Initialize default filters based on module configuration
	const initializeDefaultFilters = useMemo(() => {
		if (!moduleName) return [];

		const config = getFilterByModule(moduleName);
		if (!config) return [];

		const column = columns.find(
			(col) => col.key === config.columnKey && col.type === 'date'
		);
		if (!column) return [];

		return [
			{
				key: column.key,
				type: 'date',
				value: getYearToDateRange(column.dateISO, config.defaultDateFilter),
				operator: 'equals',
			},
		];
	}, [moduleName, columns, getFilterByModule]);
	const [pageSize, setPageSize] = useState<SelectItem>({
		label: '15',
		value: 15,
	});

	const [selection, setSelection] = useState<string[]>(selectedItems);
	const [page, setPage] = useState(1);
	const [status, setStatus] = useState<SelectItem>(hideAllStatusFilter && statusFilters.length > 0 ? statusFilters[0] : { label: 'All', value: '' });
	const [globalDateFilter, setGlobalDateFilter] = useState<SelectItem | null>(globalDateFilters.length > 0 ? globalDateFilters[0] : null);
	const [isDateFilterModalOpen, setIsDateFilterModalOpen] = useState<boolean>(false);
	const [dateRange, setDateRange] = useState<DateRange>({
		startDate: '',
		endDate: '',
	});
	const [customDateRange, setCustomDateRange] = useState<DateRange>({
		startDate: '',
		endDate: '',
	});
	const [size, setSize] = useState<number>(2);
	const [search, setSearch] = useState('');
	const [hiddenColumn, setHiddenColumn] = useState<string[]>([]);
	const [sort, setSort] = useState<SortOption | null>(null);
	const [columnsFilters, setColumnFilters] = useState<ColumnFiltersProps[]>(initializeDefaultFilters);
	const [globalFilters, setGlobalFilters] = useState<ISelectedItem[]>([]);
	const [firstRender, setFirstRender] = useState<boolean>(true);
	const [allowExpand, setAllowExpand] = useState<boolean>(true);
	const [isExportingAll, setIsExportingAll] = useState<boolean>(false);
	
	// Check if there are default filters initialized
	const hasDefaultFilters = useMemo(() => initializeDefaultFilters.length > 0, [initializeDefaultFilters]);

	const updateSearch = (search: string) => {
		setSearch(search);
		setPage(1);
	};

	const updateColumnFilters = (columnFilters: ColumnFiltersProps[]) => {
		setColumnFilters(columnFilters);
		setPage(1);
	};

	const updateStatus = (status: SelectItem) => {
		setStatus(status);
		setPage(1);
	};

	useEffect(() => {
		// Apply filters on every change, including first render if there are default filters
		if (!firstRender || hasDefaultFilters) {
			onFilterApply({
				page,
				limit: +pageSize.value,
				status,
				search,
				sort,
				columnsFilters,
				globalFilters,
				dateRange,
			});
		}
		
		if (firstRender) {
			setFirstRender(false);
		}
	}, [page, pageSize.value, status, search, sort, columnsFilters, globalFilters, dateRange]);

	// Handler to update global date filter and open modal if custom is selected
	const handleGlobalDateFilterChange = (newValue: SelectItem) => {
		setGlobalDateFilter(newValue);
		// If custom is selected, always open the modal (even if already selected)
		if (newValue?.value === 'custom') {
			setIsDateFilterModalOpen(true);
		} else if (newValue?.value) {
			setDateRange(getDateRange(newValue.value as string, dateRange, 'dateISO'));
		}
	};

	useEffect(() => {
		if (globalDateFilter?.value === 'custom') {
			setIsDateFilterModalOpen(true);
		} else if (globalDateFilter?.value) {
			setDateRange(getDateRange(globalDateFilter.value as string, dateRange, 'dateISO'));
		}
	}, [globalDateFilter]);
	
	useEffect(() => {
		onSelection(selection);
	}, [selection]);

	const filteredColumns = useMemo(() => {
		if (requiredColumnsForExpand?.length) {
			const hiddenColumns = requiredColumnsForExpand.filter(column => hiddenColumn.includes(column));
			if (hiddenColumns.length === requiredColumnsForExpand.length) {
				setAllowExpand(false);
			} else {
				setAllowExpand(true);
			}
		}
		return columns.filter((col) => !hiddenColumn.includes(col.key));
	}, [columns, hiddenColumn]);

	const exportCurrentAsCSV = () => {
		generateCSVFromTable(
			filteredColumns,
			(customExportItemsData.length && customExportItemsData) || items,
			`${tableName}-current`,
		);
	};

	const exportCurrentAsExcel = () => {
		generateExcelFromTable(
			filteredColumns,
			(customExportItemsData.length && customExportItemsData) || items,
			`${tableName}-current`,
		);
	};

	const exportCurrentAsPDF = () => {
		console.log('Exporting current data as PDF');
	};

	const handleExportAllAsCSV = exportAllAsCSV
		? async () => {
				setIsExportingAll(true);
				try {
					await scheduleBackgroundExport(async () => {
						await exportAllAsCSV();
					});
				} finally {
					setIsExportingAll(false);
				}
			}
		: undefined;

	const handleExportAllAsExcel = exportAllAsExcel
		? async () => {
				setIsExportingAll(true);
				try {
					await scheduleBackgroundExport(async () => {
						await exportAllAsExcel();
					});
				} finally {
					setIsExportingAll(false);
				}
			}
		: undefined;

	return (
		<Box
			data-id="smart-data-table"
			bg={{ base: '{colors.gray.50}', _dark: '{colors.gray.900}' }}
			ref={ref}
		>
			<Box width="100%">
				<Flex
					flexWrap="wrap"
					justify="space-between"
					align="center"
					gap={3}
					width="100%"
				>
					<Flex align="center" flex="1" minWidth='fit-content'>
						<Show when={!!topLabel}>
							<FaceliftText fontWeight={600} fontSize="x-large">
								{topLabel}
							</FaceliftText>
						</Show>
					</Flex>

					<Flex align="center" justify="flex-end" flex="1" minWidth='fit-content'>
						<Show
							when={
								searchable ||
								hideShowColumn ||
								expandable ||
								fullscreen ||
								exportable ||
								!!globalFilter ||
								!!creatableFn ||
								statusFilters.length > 0
							}
						>
							<DataTableFilters
								searchable={searchable}
								hideShowColumn={hideShowColumn}
								expandable={expandable}
								allowExpand={allowExpand}
								fullscreen={fullscreen}
								exportable={exportable}
								globalFilter={globalFilter}
								creatableFn={creatableFn}
								statusFilters={statusFilters}
								status={status}
								setStatus={updateStatus}
								size={size}
								setSize={setSize}
								search={search}
								setSearch={updateSearch}
								columns={columns}
								hiddenColumn={hiddenColumn}
								setHiddenColumn={setHiddenColumn}
								csv={csv}
								excel={excel}
								pdf={pdf}
								exportAllAsCSV={handleExportAllAsCSV}
								exportAllAsExcel={handleExportAllAsExcel}
								exportAllAsPDF={exportAllAsPDF}
								exportCurrentAsCSV={exportCurrentAsCSV}
								exportCurrentAsExcel={exportCurrentAsExcel}
								exportCurrentAsPDF={exportCurrentAsPDF}
								addButtonRef={addButtonRef}
								exportButtonRef={exportButtonRef}
								defaultGlobalFilters={globalFilters}
								onChangeGlobalFilter={setGlobalFilters}
								globalActions={globalActions}
								selectedRows={selection}
								globalDateFilters={globalDateFilters}
								globalDateFilter={globalDateFilter}
								setGlobalDateFilter={handleGlobalDateFilterChange}
								isExportingAll={isExportingAll}
							/>
						</Show>
					</Flex>
				</Flex>
			</Box>
			<Box
				mt={4}
				pt={4}
				px={4}
				pb={8}
				borderRadius="xl"
				bg={{ base: 'white', _dark: colors.tabelCellDark }}
			>
				<Table.ScrollArea height={`${(hideAllColumnFilters ? 15 : 16) * 44}px`} maxW="100%">
					<DataTableInner
						columns={filteredColumns}
						selectColumnRef={selectColumnRef}
						onSelectColumnRefReady={onSelectColumnRefReady}
						items={items}
						isSelectable={isSelectable}
						selection={selection}
						setSelection={setSelection}
						bordered={bordered}
						size={size}
						actions={actions}
						loading={loading}
						sort={sort}
						setSort={setSort}
						columnsFilters={columnsFilters}
						setColumnFilters={updateColumnFilters}
						allowExpand={allowExpand}
						actionMenuOpenOverride={actionMenuOpenOverride}
						hideAllColumnFilters={hideAllColumnFilters}
					/>
				</Table.ScrollArea>
			</Box>
			<Show when={paginated}>
				<DataTablePaginations
					page={page}
					setPage={setPage}
					pageSize={pageSize}
					setPageSize={setPageSize}
					count={totalCount}
				/>
			</Show>
			<FaceliftDialog
				title="Select Period"
				showBackBtn
				open={isDateFilterModalOpen}
				onOpenChange={(open) => {
					if (!open) {
						setIsDateFilterModalOpen(false);
					} else {
						setIsDateFilterModalOpen(true);
					}
				}}
				sx={{
					maxWidth: 400,
					maxHeight: 400,
				}}
				footer={
					<FaceliftButton
						width="100px"
						onClick={() => {
							setIsDateFilterModalOpen(false);
							setDateRange(getDateRange(globalDateFilter?.value as string, customDateRange, 'dateISO'));
						}}
						disabled={globalDateFilter?.value === 'custom' && !customDateRange.startDate && !customDateRange.endDate}
					>
						Apply
					</FaceliftButton>
				}
			>
				<FaceliftDatePicker
					isRange={true}
					range={customDateRange}
					onRangeChange={setCustomDateRange}
					placeholder="Choose period..."
				/>
			</FaceliftDialog>
		</Box>
	);
};

export default DataTable;
